import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import useMeasure from "react-use-measure";

import shipCore from "../../core/ship.core";
import { useTranslation } from "../../core/translation.core";
import ChevronDownIcon from "../../icons/chevronDown.icon";
import { Country } from "../../types/country";

type StateType = "init" | "open";

export default function CountrySelectorClick({ onSelected }: { onSelected: (country: Country) => void }) {
  const { t, msg } = useTranslation();
  const [state, setState] = useState<StateType>("init");
  const [divRef, divBounds] = useMeasure();
  const countrySelectRef = useRef<HTMLDivElement>(null);
  // const [selectedCountry, setSelectedCountry] = useState<Country>(null);
  const [searchingText, setSearchingText] = useState("");

  const { locale } = useRouter();

  const countryListQueryRst = useQuery({
    queryKey: ["countryList", locale],
    queryFn: shipCore.getCountryList,
  });

  useEffect(() => {
    document.addEventListener("click", (e) => {
      // This is a hack to get the latest state
      if (!countrySelectRef || !countrySelectRef.current) return;
      const state = (countrySelectRef.current as any).getAttribute("data-state");

      if (state !== "open") return;

      const isWithin = (e.target as any).closest("#country-select");

      if (!isWithin) {
        setState("init");
      }
    });
  }, []);

  return (
    <div id="country-select" className="border relative" data-state={state} ref={countrySelectRef}>
      <div
        className="flex justify-between items-center pr-6 pl-4 hover:bg-gray-100 hover:dark:bg-gray-800 cursor-pointer"
        onClick={() => setState("open")}
      >
        <div ref={divRef}>
          <label>
            <span className="block cursor-pointer">{t(msg.country.country)}</span>
            {/* {state === "selected" ? (
              <span className="cursor-pointer">{selectedCountry ? selectedCountry.name : ""}</span>
            ) : null} */}
            {/* <input {...useFormMethods.register(fieldName)} type="hidden" /> */}
            <span className="cursor-pointer">{t(msg.country.pleaseSelect)}</span>
          </label>
        </div>
        <div>
          <ChevronDownIcon />
        </div>
      </div>
      {state === "open" ? (
        <div className="absolute left-0 w-full bg-white dark:bg-her-darkSectionBg" style={{ top: divBounds.height }}>
          <div className="h-64 border overflow-y-auto">
            <SearchBox state={state} setSearchingText={setSearchingText} />
            <CountryList
              searchingText={searchingText}
              // setSelectedCountry={setSelectedCountry}
              countrySelected={(country) => onSelected(country)}
              setState={setState}
              countryListQueryRst={countryListQueryRst}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const SearchBox = ({ state, setSearchingText }: { state: StateType; setSearchingText: Dispatch<SetStateAction<string>> }) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t, msg } = useTranslation();

  useEffect(() => {
    function focusInputBox() {
      if (state === "open" && searchInputRef && searchInputRef.current) {
        (searchInputRef.current as any).focus();
      }
    }

    focusInputBox();
  }, [state, searchInputRef]);

  return (
    <div className="absolute top-0 left-0 w-full px-2 py-2 dark:bg-her-darkSectionBg border-r border-l">
      <input
        ref={searchInputRef}
        id="country-select-search"
        className="w-full pl-4 rounded-lg h-8 dark:bg-her-backblue"
        placeholder={t(msg.country.search)}
        onChange={(e) => {
          setSearchingText(e.target.value);
        }}
      />
    </div>
  );
};

function countryPredicate(country: Country, searchingText: string) {
  const regex = new RegExp(searchingText, "i");
  return regex.test(country.iso) || regex.test(country.name) || regex.test(country.code);
}

const CountryList = ({
  searchingText,
  setState,
  countrySelected,
  countryListQueryRst,
}: {
  searchingText: string;
  setState: Dispatch<SetStateAction<StateType>>;
  countrySelected: (country: Country) => void;
  countryListQueryRst: UseQueryResult<Country[], Error>;
}) => {
  const { isLoading, error, isError, data } = countryListQueryRst;

  if (isLoading) return null;
  if (isError) return <div className="mt-12">{error.message}</div>;

  const filteredList = searchingText ? data!.filter((c) => countryPredicate(c, searchingText)) : data!;

  return (
    <div className="mt-12 dark:bg-her-darkSectionBg" id="country-select-list">
      <ul>
        {filteredList.map((c) => (
          <li key={c.iso}>
            <button
              className="pl-6 py-2 cursor-pointer hover:bg-gray-100 hover:dark:bg-gray-800 w-full text-left"
              onClick={() => {
                countrySelected(c);
                // setSelectedCountry(c);
                setState("init");
              }}
            >
              {c.name}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
